import { Component, OnInit, Input, ViewChild, signal, inject } from '@angular/core';
import { ConstantService } from 'src/app/services/common/constant.service';
import { DataTableDirective } from 'angular-datatables';
import { TranslateService } from '@ngx-translate/core';

/* 
  Notes for using this component

  1. Give nessecery settings by inputing a DataTableConfig
  2. tableOptions will overwrite the default options if provided
  3. Add 'className' and 'title' to tableColumns items for html display
  4. Use .bind(this) for row-click and button behaviour

*/

export class DataTableConfig {
  // Required
  tableID: string;
  loadTableData: (dataTablesParameters: any, callback: any) => void;
  tableColumns: any[];

  // Optional
  tableOptions?: any;
  tableButtons?: any[] = [];
  tableRowCallback?: any[] = [];
  createdRow?: (row: any, data: any, index: any) => void;
  initComplete?: () => void;

  constructor(datatableConfig?: Partial<DataTableConfig>) {
    Object.assign(this, datatableConfig);
  }
}

@Component({
  standalone: false,
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
})
export class DatatableComponent implements OnInit{

  @Input({ required: true }) config: DataTableConfig;
  @Input() showPageAll: boolean;
  
  @ViewChild(DataTableDirective, { static: false }) datatableElement: DataTableDirective;

  public data: any[] = [];
  public dtOptions: any = {};
  
  protected initialised = signal(true);
  protected selectAllOpt = signal(false);
  protected isAllSelected = signal(false);

  private selectedRows: Set<string> = new Set();
  private lastPage: number = 0;
  private lastSearchText = '';
  private pageLength = 10;
  
  private constantService = inject(ConstantService);
  private translate = inject(TranslateService);

  ngOnInit(): void {
    this.initializeDataTable();
    this.initialised.set(true);
  }
  
  public getSelectedRows(){
    return Array.from(this.selectedRows).map(item => JSON.parse(item));
  }

  public isSelectedAnyRows(){
    return this.selectedRows.size > 0;
  };

  public reload(): void {
    this.selectedRows.clear();
    this.isAllSelected.set(false);
    this.initialised.set(false);
    setTimeout(() => this.initialised.set(true), 1);
  }

  protected selectAllRowsInCurrentPage(e) {
    this.datatableElement.dtInstance.then((dtInstance: any) => {
      if (this.isAllSelected()) {
        dtInstance.rows().every((rowIdx) => {
          const row = dtInstance.row(rowIdx);
          row.deselect();
          this.removeSelectedRows(row.data());
        });
        this.isAllSelected.set(false);
      } else {
        dtInstance.rows().every((rowIdx) => {
          const row = dtInstance.row(rowIdx);
          if(!row.data().isDisableCheckbox){
            row.select();
            this.addSelectedRows(row.data());
          }
        });
        this.isAllSelected.set(true);
      }
    });
  }

  private initializeDataTable() {
    if(this.config.tableOptions && this.config.tableOptions.select?.selectAll){
      this.selectAllOpt.set(true);
    }

    const defaultOptions = {
      language: this.constantService.TableLang,
      info: true,
      pagingType: 'full_numbers',
      start: 0,
      length: 1,
      pageLength: this.pageLength,
      displayStart: this.lastPage,
      search: { search: this.lastSearchText },
      autoWidth: false,
      ordering: true,
      order: [[0, 'asc']],
      lengthMenu: [
        [5, 10, 20, 50],
        [5, 10, 20, 50]
      ],
      dom: 'lBfrtip',
      select: {},
      serverSide: true,
      processing: true,
      stateSave: true,
      stateSaveCallback: this.constantService.saveTableState,
      stateLoadCallback: this.constantService.loadTableState,
    };

    this.checkPaging(defaultOptions);

    if (this.showPageAll){
      defaultOptions.lengthMenu[0].push(1000000000)
      defaultOptions.lengthMenu[1].push(this.translate.instant('All'))
    }

    this.dtOptions = { ...defaultOptions, ...this.config.tableOptions};

    if(this.dtOptions.select?.style && !this.dtOptions.order){
      this.dtOptions.order = [[1, 'asc']];
    }

    // === columns ==================
    this.dtOptions.columns = this.dtOptions.select?.style
      ? [
          {
            checkboxes: {
              selectRow: true,
              stateSave: false,
              selectAll: false
            },
            data: this.dtOptions.select?.uniqueId,
            className: 'select-checkbox wd-5p',
            name: 'isSelect',
            targets: 0,
            defaultContent: '',
            orderable: false,
            sortable: false,
            searchable: false,
            render: () => '<span class="invisible"></span>',
          },
          ...this.config.tableColumns,
        ]
      : this.config.tableColumns;

    // === buttons ==================
    this.dtOptions.buttons = this.config.tableButtons.map(button => {
      return {
        text: button.text,
        attr: button.attr,
        action: (e, dt, node, config) => {
          button.action(e, dt, node, config);
        }
      };
    });

    // === ajax ==================
    this.dtOptions.ajax = (dataTablesParameters: any, callback) => {
      this.checkPaging(dataTablesParameters);
      this.data = dataTablesParameters.data;
      this.config.loadTableData(dataTablesParameters, callback);
    };

    // === rowCallback ==================
    this.dtOptions.rowCallback = (row: Node, data: any[] | Object, index: number) => {
      this.config.tableRowCallback.forEach(action => {
        const element = $(action.selector, row);
        element.off('click');
        element.on('click', (event) => {
          event.stopPropagation(); // avoid other actions
          action.callback(data, event);
        });
      });

      if(this.dtOptions.select?.style && Object(data).isDisableCheckbox){
        $(row).prop('disabled', true);
        $(row).children().removeClass('select-checkbox');
      }

      // for user-list merged user only
      if(this.config.tableID=='user-table'){
        this.config.tableOptions.childRowClick(row,data);
      }
      return row;
    };

    // === drawCallback ==================
    if(this.dtOptions.select?.style){
      // Update "checked-all" checkbox when navigating the page
      const parent = this;
      this.dtOptions.drawCallback = function (settings) {
        const api = this.api();
        setTimeout(() => {
          parent.isAllSelected.set(api.rows({ selected: true }).count() === api.page.len());
        });
      };
    }

    // === createdRow ==================
    if(this.config.createdRow){
      this.dtOptions.createdRow = (row: any, data: any, index: any) => {
        this.config.createdRow(row, data, index);
      }
    }

    this.dtOptions.initComplete = () => {
      this.datatableElement.dtInstance.then(dt => {
        // Update "selectedRows" and "checked-all" checkbox when manually selected
        dt.on('select', (e, dt, type, indexes) => {
          if(!dt.data().selector){
            this.addSelectedRows(dt.data());
            this.isAllSelected.set(dt.rows({ selected: true }).indexes().length === dt.page.len());
          }
        });
        dt.on('deselect', (e, dt, type, indexes) => {
          if(!dt.data().selector){
            this.removeSelectedRows(dt.data());
            this.isAllSelected.set(false);
          }
        });
      })
      // If a custom initComplete is provided
      if(this.config.initComplete){
        this.config.initComplete();
      }
    }
  }

  private checkPaging(dataTablesParameters){
    if (!dataTablesParameters.length || isNaN(dataTablesParameters.length)){
      dataTablesParameters.length = 1000000000;
    }

    if (!dataTablesParameters.start || isNaN(dataTablesParameters.start)){
      dataTablesParameters.start = 0;
    }
  }

  private addSelectedRows(data:any){
    const str = JSON.stringify(data);

    if(this.dtOptions.select?.style == 'single'){
      this.selectedRows.clear();
      this.selectedRows.add(str);
      return;
    }

    if(!this.selectedRows.has(str)){
      this.selectedRows.add(str);
    }
  }

  private removeSelectedRows(data:any){
    const str = JSON.stringify(data);
    this.selectedRows.delete(str);
  }

}
